import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { Disposable } from '@patsnap/tahiti-core';
import type { Config, DriveStep, Driver } from 'driver.js';
import {
    OnboardingEligibilityService,
    type OnboardingEligibilityService as OnboardingEligibilityServiceType,
} from '../../common/services/onboarding-eligibility-service';
import {
    OnboardingActionGuideService,
    type OnboardingActionGuideService as OnboardingActionGuideServiceType,
} from '../../common/services/onboarding-action-guide-service';
import {
    OnboardingInputExampleGuidePresenter,
    type OnboardingInputExampleGuidePresenter as OnboardingInputExampleGuidePresenterType,
} from '../../common/services/onboarding-input-example-guide-presenter';
import {
    OnboardingStepTwoGuideService,
    type OnboardingStepTwoGuideService as OnboardingStepTwoGuideServiceType,
} from '../../common/services/onboarding-step-two-guide-service';
import {
    HomeV2EntryOnboardingService,
    type HomeV2EntryOnboardingService as HomeV2EntryOnboardingServiceType,
} from '../../home-v2/home-v2-entry-onboarding-service';
import {
    isInteractionTargetWithin,
    listenForActiveRegionInteraction,
} from '../../common/utils/active-region-interaction';
import {
    DesignFtoOnboardingRuntime,
    type DesignFtoOnboardingRuntime as DesignFtoOnboardingRuntimeType,
} from '../design-fto-onboarding-runtime';
import type {
    DesignFtoOnboardingService,
    DesignFtoOnboardingStartOptions,
} from '../design-fto-onboarding-service';

/** 上传/文本输入区与无历史示例共同组成的整体高亮区域。 */
const GROUP_SELECTOR = '[data-testid="design-fto-onboarding-group"]';
/** Design FTO 完整上传/文本输入区的稳定选择器。 */
const INPUT_SELECTOR = '[data-testid="design-fto-onboarding-input"]';
/** Design FTO 首页实际纵向滚动容器的稳定选择器。 */
const SCROLL_CONTAINER_SELECTOR = '[data-testid="design-fto-onboarding-scroll-container"]';
/** 第一个示例手势目标的稳定选择器。 */
const EXAMPLE_SELECTOR = '[data-testid="design-fto-onboarding-example"]';
/** 等待示例异步渲染的最长时间。 */
const EXAMPLE_WAIT_TIMEOUT_MS = 5000;
/** 最终提交按钮的稳定选择器。 */
const SUBMIT_SELECTOR = '[data-testid="design-fto-onboarding-submit"]';
/** 图片上传完成后展示提交引导前的缓冲时间。 */
const SUBMIT_GUIDE_DELAY_MS = 2000;
/** URL 图片预填后停止编辑到重新提示提交的等待时间。 */
const PREFILLED_SUBMIT_GUIDE_DELAY_MS = 3000;
/** Driver 启动前解析得到的可用目标。 */
interface OnboardingTargets {
    /** 上传/文本输入区与可选示例共同组成的高亮容器。 */
    groupElement: Element;
    /** 提交阶段需要单独浮出遮罩的上传/文本输入区。 */
    inputElement: Element;
    /** 首段引导激活期间需要锁定的首页纵向滚动容器。 */
    scrollContainerElement: Element;
    /** 可选的第一个示例辅助目标。 */
    exampleElement?: Element;
}

/** 提交按钮引导激活前必须存在的页面目标。 */
interface SubmitGuideTargets {
    /** 需要挂载公共点击手势的提交按钮。 */
    submitElement: Element;
}

/**
 * Design FTO 首页首次引导编排器。
 *
 * 负责串联上传/示例 Driver 和提交按钮引导，并隔离页面生命周期。
 */
@injectable()
export class DesignFtoOnboardingServiceImpl implements DesignFtoOnboardingService {
    /** 当前上传/示例功能引导的 Driver。 */
    private _driver?: Driver;
    /** 首条示例双击单次演示的清理句柄。 */
    private _inputExampleGuideDisposable?: Disposable;
    /** 当前首页纵向滚动锁的清理句柄。 */
    private _scrollLockDisposable?: Disposable;
    /** 高亮区域内任意原生指针交互的清理句柄。 */
    private _activeRegionInteractionDisposable?: Disposable;
    /** 当前是否已有尚未完成的启动流程。 */
    private _isStarting = false;
    /** 上传/示例交互后是否允许在上传完成时展示提交引导。 */
    private _isSubmitGuideArmed = false;
    /** 当前是否正在展示 URL 图片预填的 Step 2。 */
    private _isPrefilledInputGuideActive = false;
    /** 当前提交引导是否源于 URL 图片预填分支。 */
    private _isPrefilledInputFlow = false;
    /** 当前页面生命周期内是否已经展示过提交按钮引导。 */
    private _hasShownSubmitGuide = false;
    /** 提交遮罩和手势是否已完整激活，可用于一次归因。 */
    private _isSubmitGuideActive = false;
    /** 当前提交按钮循环点击手型的清理句柄。 */
    private _submitGuideDisposable?: Disposable;
    /** 等待展示提交按钮引导的定时器。 */
    private _submitGuideTimerId?: number;
    /** 用于使页面销毁前的异步流程失效。 */
    private _lifecycleId = 0;

    // eslint-disable-next-line max-params
    constructor(
        /** 封装 Driver、DOM 和定时器等浏览器能力。 */
        @inject(DesignFtoOnboardingRuntime)
        private readonly _runtime: DesignFtoOnboardingRuntimeType,
        /** 统一判断当前账号的受众和外观 FTO 任务历史资格。 */
        @inject(OnboardingEligibilityService)
        private readonly _onboardingEligibilityService: OnboardingEligibilityServiceType,
        /** 动态挂载最终提交按钮的循环点击手型。 */
        @inject(OnboardingActionGuideService)
        private readonly _onboardingActionGuideService: OnboardingActionGuideServiceType,
        /** 挂载不触发真实点击的首条图片示例双击手势。 */
        @inject(OnboardingInputExampleGuidePresenter)
        private readonly _inputExampleGuidePresenter: OnboardingInputExampleGuidePresenterType,
        /** 统一在当前 Agent 输入引导前展示 Home V2 Step 1。 */
        @inject(HomeV2EntryOnboardingService)
        private readonly _homeV2EntryOnboardingService: HomeV2EntryOnboardingServiceType,
        /** 统一生成 Step 2 气泡文案并记录曝光。 */
        @inject(OnboardingStepTwoGuideService)
        private readonly _onboardingStepTwoGuideService: OnboardingStepTwoGuideServiceType,
    ) {}

    /** 按当前页面、用户和展示状态尝试启动首次引导。 */
    async start(options: DesignFtoOnboardingStartOptions): Promise<boolean> {
        if (this._skipPrefilledInput(options)) return false;
        if (!this._canStart(options)) return false;
        const lifecycleId = ++this._lifecycleId;
        this._isStarting = true;
        try {
            const canStartInputGuide = await this._homeV2EntryOnboardingService
                .acquireInputGuide('design-fto');
            if (!this._isCurrent(lifecycleId) || !canStartInputGuide) return false;
            if (this._skipPrefilledInput(options)) return false;
            return await this._start(options, lifecycleId);
        } catch (error) {
            this._runtime.warn('Failed to start Design FTO onboarding:', error);
            return false;
        } finally {
            if (this._isCurrent(lifecycleId)) {
                this._isStarting = false;
            }
        }
    }

    /** 用户点击上传区或添加文件时关闭当前功能引导。 */
    notifyUploadSelected(): void {
        if (this._isPrefilledInputGuideActive) {
            this._preparePrefilledInputEditing();
            return;
        }
        this._dismissActiveDriverForSubmitGuide();
    }

    /** 用户选择示例时关闭当前功能引导。 */
    notifyExampleSelected(): void {
        this._dismissActiveDriverForSubmitGuide();
    }

    /** 图片上传完成且提交按钮可用时，延迟展示一次操作引导。 */
    notifyUploadReady(): void {
        this._scheduleSubmitGuide(
            this._isPrefilledInputFlow
                ? PREFILLED_SUBMIT_GUIDE_DELAY_MS
                : SUBMIT_GUIDE_DELAY_MS,
        );
    }

    /** 聚焦 URL 图片预填说明时结束高亮，并在停止编辑三秒后恢复提交提示。 */
    notifyInputFocused(): void {
        if (!this._isPrefilledInputGuideActive) return;
        this._preparePrefilledInputEditing();
        this._scheduleSubmitGuide(PREFILLED_SUBMIT_GUIDE_DELAY_MS);
    }

    /** 修改 URL 图片预填说明时重置提交提示的三秒防抖。 */
    notifyManualInput(): void {
        if (!this._isPrefilledInputFlow) return;
        if (this._isPrefilledInputGuideActive) {
            this._preparePrefilledInputEditing();
        } else {
            this._isSubmitGuideArmed = true;
            this._hasShownSubmitGuide = false;
            this._clearSubmitGuide();
        }
        this._scheduleSubmitGuide(PREFILLED_SUBMIT_GUIDE_DELAY_MS);
    }

    /** 消费一次仍处于激活状态的提交引导归因，并立即清理视觉效果。 */
    consumeSubmitGuide(): boolean {
        if (!this._isSubmitGuideActive) return false;

        this._isSubmitGuideArmed = false;
        this._isPrefilledInputFlow = false;
        this._clearSubmitGuide();
        return true;
    }

    /** 销毁当前页面生命周期内的 Driver 和辅助高亮。 */
    destroy(): void {
        this._lifecycleId += 1;
        this._isStarting = false;
        this._isSubmitGuideArmed = false;
        this._isPrefilledInputGuideActive = false;
        this._isPrefilledInputFlow = false;
        this._clearSubmitGuide();
        this._dismissActiveDriver();
    }

    /** 带入输入时同步公共跳过状态，并清理本 Agent 的待决引导。 */
    private _skipPrefilledInput(options: DesignFtoOnboardingStartOptions): boolean {
        if (!options.getHasPrefilledInput()) return false;
        this._homeV2EntryOnboardingService.setEntryContext({ hasPrefilledInput: true });
        this.destroy();
        return true;
    }

    /** 校验页面状态并执行功能引导。 */
    private async _start(
        options: DesignFtoOnboardingStartOptions,
        lifecycleId: number,
    ): Promise<boolean> {
        if (options.getIsAutoRunning()) return false;
        if (options.getHasPrefilledInput()) {
            const targets = await this._resolvePrefilledInputTargets(options, lifecycleId);
            if (!targets) return false;
            return this._activatePrefilledInputDriver(targets, lifecycleId);
        }
        return this._startUploadGuide(options, lifecycleId);
    }

    /** 启动上传/示例功能引导。 */
    private async _startUploadGuide(
        options: DesignFtoOnboardingStartOptions,
        lifecycleId: number,
    ): Promise<boolean> {
        if (!this._canShowUploadGuide(options, lifecycleId)) return false;

        const targets = await this._resolveTargets(options, lifecycleId);
        if (!targets || this._skipPrefilledInput(options)) return false;
        return this._activateDriver(targets, lifecycleId);
    }

    /** 解析必须存在的整体输入区、滚动容器，以及当前页面可选的第一个示例。 */
    private async _resolveTargets(
        options: DesignFtoOnboardingStartOptions,
        lifecycleId: number,
    ): Promise<OnboardingTargets | undefined> {
        await this._runtime.waitForRender();
        const requiredTargets = this._resolveRequiredTargets(lifecycleId);
        if (!requiredTargets) return;
        if (!this._shouldShowOnCurrentPage(options)) return;

        const exampleElement = await this._runtime.waitForElement(
            EXAMPLE_SELECTOR,
            EXAMPLE_WAIT_TIMEOUT_MS,
        );
        if (!this._shouldShowOnCurrentPage(options)) return;
        const currentRequiredTargets = this._resolveRequiredTargets(lifecycleId);
        if (!currentRequiredTargets) return;
        return {
            ...currentRequiredTargets,
            exampleElement: exampleElement ?? undefined,
        };
    }

    /** 读取当前生命周期内必须存在的整体区域、输入区和纵向滚动容器。 */
    private _resolveRequiredTargets(lifecycleId: number): OnboardingTargets | undefined {
        if (!this._isCurrent(lifecycleId)) return;
        const groupElement = this._runtime.querySelector(GROUP_SELECTOR);
        const inputElement = this._runtime.querySelector(INPUT_SELECTOR);
        const scrollContainerElement = this._runtime.querySelector(SCROLL_CONTAINER_SELECTOR);
        if (!groupElement || !inputElement || !scrollContainerElement) return;
        return { groupElement, inputElement, scrollContainerElement };
    }

    /** 等待 URL 图片预填分支需要的输入、推荐区域和滚动容器渲染完成。 */
    private async _resolvePrefilledInputTargets(
        options: DesignFtoOnboardingStartOptions,
        lifecycleId: number,
    ): Promise<Pick<OnboardingTargets, 'groupElement' | 'inputElement' | 'scrollContainerElement'> | undefined> {
        await this._runtime.waitForRender();
        if (!this._canShowUploadGuide(options, lifecycleId)) return;
        const groupElement = this._runtime.querySelector(GROUP_SELECTOR);
        const inputElement = this._runtime.querySelector(INPUT_SELECTOR);
        const scrollContainerElement = this._runtime.querySelector(SCROLL_CONTAINER_SELECTOR);
        if (!groupElement || !inputElement || !scrollContainerElement) return;
        return { groupElement, inputElement, scrollContainerElement };
    }

    /** 创建并激活上传/示例 Driver，成功后记录功能阶段。 */
    private _activateDriver(
        targets: OnboardingTargets,
        lifecycleId: number,
    ): boolean {
        const steps = this._createSteps();
        try {
            const driverInstance = this._runtime.createDriver(
                this._createDriverConfig(lifecycleId, steps),
            );
            this._driver = driverInstance;
            driverInstance.highlight(steps[0]);
            if (!driverInstance.isActive()) {
                this._dismissActiveDriver();
                return false;
            }
            this._scrollLockDisposable = this._runtime.lockVerticalScroll(targets.scrollContainerElement);
        } catch (error) {
            this._dismissActiveDriver();
            this._runtime.warn('Failed to activate Design FTO upload guide:', error);
            return false;
        }
        this._bindActiveRegionInteraction(targets.groupElement);
        this._mountInputExampleGuide(targets);
        this._onboardingEligibilityService.markEntryOnboardingSeen();
        this._onboardingStepTwoGuideService.trackSpotlight('design-fto', false);
        return true;
    }

    /** 激活 URL 图片预填说明，并立即提示点击已可用的提交按钮。 */
    private _activatePrefilledInputDriver(
        targets: Pick<OnboardingTargets, 'groupElement' | 'inputElement' | 'scrollContainerElement'>,
        lifecycleId: number,
    ): boolean {
        const step = this._onboardingStepTwoGuideService.createStep(GROUP_SELECTOR, true);
        try {
            const driverInstance = this._runtime.createDriver(
                this._createDriverConfig(lifecycleId, [step]),
            );
            this._driver = driverInstance;
            driverInstance.highlight(step);
            if (!driverInstance.isActive()) {
                this._dismissActiveDriver();
                return false;
            }
            this._scrollLockDisposable = this._runtime.lockVerticalScroll(
                targets.scrollContainerElement,
            );
            this._bindActiveRegionInteraction(targets.groupElement);
        } catch (error) {
            this._dismissActiveDriver();
            this._runtime.warn('Failed to activate Design FTO prefilled input guide:', error);
            return false;
        }

        this._isPrefilledInputGuideActive = true;
        this._isPrefilledInputFlow = true;
        this._isSubmitGuideArmed = true;
        this._showSubmitGuide();
        this._onboardingEligibilityService.markEntryOnboardingSeen();
        this._onboardingStepTwoGuideService.trackSpotlight('design-fto', true);
        return true;
    }

    /** 创建上传/文本输入区整体目标的 Driver 配置。 */
    private _createDriverConfig(lifecycleId: number, steps = this._createSteps()): Config {
        return {
            animate: true,
            duration: 300,
            overlayColor: '#0B2447',
            overlayOpacity: 0.56,
            smoothScroll: false,
            allowClose: true,
            allowScroll: false,
            overlayClickBehavior: () => undefined,
            stagePadding: 6,
            stageRadius: 10,
            disableActiveInteraction: false,
            allowKeyboardControl: true,
            onDestroyed: (_element, _step, { driver: destroyedDriver }) => {
                if (this._driver === destroyedDriver) {
                    const wasPrefilledInputGuide = this._isPrefilledInputGuideActive;
                    this._driver = undefined;
                    this._isPrefilledInputGuideActive = false;
                    this._disposeInputExampleGuide();
                    this._disposeActiveRegionInteraction();
                    this._releaseScrollLock();
                    if (wasPrefilledInputGuide && this._isCurrent(lifecycleId)) {
                        this._isPrefilledInputFlow = false;
                        this._isSubmitGuideArmed = false;
                        this._clearSubmitGuide();
                    }
                }
                if (!this._isCurrent(lifecycleId)) return;
            },
            steps,
        };
    }

    /** 创建以上传/文本输入区整体为目标的单步引导。 */
    private _createSteps(): DriveStep[] {
        return [this._onboardingStepTwoGuideService.createStep(GROUP_SELECTOR, false)];
    }

    /** 无历史示例存在时挂载不触发真实点击的单次双击手势。 */
    private _mountInputExampleGuide(targets: OnboardingTargets): void {
        if (!targets.exampleElement) return;
        try {
            this._inputExampleGuideDisposable = this._inputExampleGuidePresenter.show({
                exampleElement: targets.exampleElement,
                groupElement: targets.groupElement,
                mode: 'gesture-only',
            });
        } catch (error) {
            this._runtime.warn('Failed to mount Design FTO example guide:', error);
        }
    }

    /** 幂等卸载首条示例单次手势。 */
    private _disposeInputExampleGuide(): void {
        this._inputExampleGuideDisposable?.dispose();
        this._inputExampleGuideDisposable = undefined;
    }

    /** 高亮区域内发生任意指针交互时先关闭 Driver，并允许后续业务事件衔接提交提示。 */
    private _bindActiveRegionInteraction(element: Element): void {
        this._activeRegionInteractionDisposable = listenForActiveRegionInteraction(
            this._runtime,
            element,
            (event) => {
                if (!this._driver?.isActive()) return;
                const isSubmitInteraction = isInteractionTargetWithin(
                    event,
                    SUBMIT_SELECTOR,
                );
                const wasPrefilledInputGuide = this._isPrefilledInputGuideActive;
                this._isSubmitGuideArmed = !isSubmitInteraction;
                if (wasPrefilledInputGuide && !isSubmitInteraction) {
                    this._hasShownSubmitGuide = false;
                    this._clearSubmitGuide();
                }
                this._dismissActiveDriver();
                if (wasPrefilledInputGuide && !isSubmitInteraction) {
                    this._scheduleSubmitGuide(PREFILLED_SUBMIT_GUIDE_DELAY_MS);
                }
            },
        );
    }

    /** 幂等移除高亮区域交互监听。 */
    private _disposeActiveRegionInteraction(): void {
        this._activeRegionInteractionDisposable?.dispose();
        this._activeRegionInteractionDisposable = undefined;
    }

    /** 立即关闭当前 Driver，并同步清理辅助目标。 */
    private _dismissActiveDriver(): void {
        const driverInstance = this._driver;
        this._driver = undefined;
        this._isPrefilledInputGuideActive = false;
        this._disposeInputExampleGuide();
        this._disposeActiveRegionInteraction();
        this._releaseScrollLock();
        driverInstance?.destroy();
    }

    /** 解除首页纵向滚动锁，并允许用户继续浏览页面。 */
    private _releaseScrollLock(): void {
        this._scrollLockDisposable?.dispose();
        this._scrollLockDisposable = undefined;
    }

    /** 仅当用户在首段 Driver 激活时选择上传或示例，才开启提交引导。 */
    private _dismissActiveDriverForSubmitGuide(): void {
        if (this._driver?.isActive()) {
            this._isSubmitGuideArmed = true;
        }
        this._dismissActiveDriver();
    }

    /** 结束预填输入高亮，并允许停止编辑三秒后重新展示提交提示。 */
    private _preparePrefilledInputEditing(): void {
        this._isSubmitGuideArmed = true;
        this._hasShownSubmitGuide = false;
        this._clearSubmitGuide();
        this._dismissActiveDriver();
    }

    /** 在图片就绪后安排一次提交按钮操作引导。 */
    private _scheduleSubmitGuide(delayMs = SUBMIT_GUIDE_DELAY_MS): void {
        if (!this._isSubmitGuideArmed
            || this._hasShownSubmitGuide
            || this._submitGuideTimerId !== undefined) {
            return;
        }

        const lifecycleId = this._lifecycleId;
        this._submitGuideTimerId = this._runtime.setTimeout(() => {
            this._submitGuideTimerId = undefined;
            if (!this._isCurrent(lifecycleId) || !this._isSubmitGuideArmed) return;
            this._showSubmitGuide();
        }, delayMs);
    }

    /** 为可用的最终提交按钮展示循环点击手型，不创建会阻断页面交互的遮罩。 */
    private _showSubmitGuide(): void {
        const targets = this._resolveSubmitGuideTargets();
        if (!targets) return;

        try {
            this._submitGuideDisposable = this._onboardingActionGuideService.show([
                targets.submitElement,
            ]);
        } catch (error) {
            this._clearSubmitGuide();
            this._runtime.warn('Failed to show Design FTO submit guide:', error);
            return;
        }
        this._isSubmitGuideArmed = false;
        this._isSubmitGuideActive = true;
        this._hasShownSubmitGuide = true;
    }

    /** 获取提交按钮循环手势所需的页面目标。 */
    private _resolveSubmitGuideTargets(): SubmitGuideTargets | undefined {
        const submitElement = this._runtime.querySelector(SUBMIT_SELECTOR);
        if (!submitElement) return;
        if (!this._runtime.isElementEnabled(submitElement)) return;
        return { submitElement };
    }

    /** 清理提交引导的等待定时器和视觉资源。 */
    private _clearSubmitGuide(): void {
        if (this._submitGuideTimerId !== undefined) {
            this._runtime.clearTimeout(this._submitGuideTimerId);
            this._submitGuideTimerId = undefined;
        }
        this._submitGuideDisposable?.dispose();
        this._submitGuideDisposable = undefined;
        this._isSubmitGuideActive = false;
    }

    /** 判断当前 service 和页面状态是否允许创建新的启动流程。 */
    private _canStart(options: DesignFtoOnboardingStartOptions): boolean {
        return this._shouldShowOnCurrentPage(options)
            && !this._isStarting
            && !this._driver?.isActive();
    }

    /** 免费提示结束后再次校验页面和功能阶段状态。 */
    private _canShowUploadGuide(
        options: DesignFtoOnboardingStartOptions,
        lifecycleId: number,
    ): boolean {
        return this._isCurrent(lifecycleId)
            && this._shouldShowOnCurrentPage(options);
    }

    /** 判断当前页面仍为历史加载完成后的 Design FTO 首页。 */
    private _shouldShowOnCurrentPage(options: DesignFtoOnboardingStartOptions): boolean {
        return !options.getHasTaskId()
            && !options.getIsHistoryLoading();
    }

    /** 判断异步回调是否仍属于当前页面生命周期。 */
    private _isCurrent(lifecycleId: number): boolean {
        return this._lifecycleId === lifecycleId;
    }

}
